import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logger from '../../utils/logger';
import { 
  BarChart3, 
  Users, 
  Phone, 
  Settings, 
  LogOut, 
  X,
  Menu,
  Bell,
  Search,
  ChevronLeft,
  ChevronRight,
  Star,
  MessageSquare,
  Calendar,
  Target,
  BookOpen,
  Mail,
  Linkedin,
  Shield,
  ChevronDown,
  User,
  Edit3,
  Check,
  Loader2,
  Eye,
  XCircle,
  Lock,
  Radio,
  Globe
} from 'lucide-react';
import { getRoleDisplayName, getRoleBadgeColor, PERMISSIONS } from '../../config/rbac';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { useRBAC } from '../../contexts/RBACContext';
import { useRBACData } from '../../hooks/useRBACData';
import { useActivityTracking } from '../../contexts/ActivityTrackingContext';
import ChatAgent from '../chat/ChatAgent';

const Layout = ({ children, currentPage, onPageChange }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [visitedPages, setVisitedPages] = useState(() => {
    const saved = localStorage.getItem('visitedPages');
    return saved ? JSON.parse(saved) : {};
  });
  const userMenuRef = useRef(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [profileImageError, setProfileImageError] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const { currentUser, logout, updateDisplayName } = useAuth();
  const { getDashboardStats, leads: allLeads, leadEvents: allLeadEvents, markNotificationAsRead, markAllNotificationsAsRead, isNotificationRead, getUnreadNotificationCount } = useData();
  const { canAccessPage, isAdminOrHigher, userRole, hasPermission, isImpersonating, impersonatedUser, stopImpersonation } = useRBAC();
  // Use RBAC-filtered data for notifications - users only see notifications from their assigned campaigns
  const { leads: rbacLeads, leadEvents: rbacLeadEvents } = useRBACData();
  // Use RBAC-filtered data for notifications
  const leads = rbacLeads;
  const leadEvents = rbacLeadEvents;
  const stats = getDashboardStats();

  // Activity tracking - only available when user is authenticated
  const activityTracking = useActivityTracking();
  const { trackPageVisit, trackButtonClick, trackLogout } = activityTracking || {};

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track page visits for activity monitoring
  useEffect(() => {
    if (currentPage && trackPageVisit) {
      trackPageVisit(currentPage);
    }
  }, [currentPage, trackPageVisit]);

  // Track visited pages
  useEffect(() => {
    if (currentPage && (currentPage === 'bookings' || currentPage === 'inbox')) {
      const newVisitedPages = { ...visitedPages, [currentPage]: true };
      setVisitedPages(newVisitedPages);
      localStorage.setItem('visitedPages', JSON.stringify(newVisitedPages));
    }
  }, [currentPage, visitedPages]);

  // Full navigation list - will be filtered by RBAC
  const allNavigation = [
    { name: 'Dashboard', icon: BarChart3, key: 'dashboard', badge: null },
    { name: 'Leads', icon: Users, key: 'leads', badge: null },
    { name: 'Call List', icon: Phone, key: 'calllist', badge: null },
    { name: 'Campaigns', icon: Target, key: 'campaigns', badge: null },
    { name: 'LinkedIn', icon: Linkedin, key: 'linkedin-automation', badge: 'NEW' },
    { name: 'Calendar', icon: Calendar, key: 'calendar', badge: null },
    { name: 'Bookings', icon: BookOpen, key: 'bookings', badge: visitedPages.bookings ? null : 'NEW' },
    { name: 'Inbox', icon: MessageSquare, key: 'inbox', badge: visitedPages.inbox ? null : leadEvents.filter(e => e.event_type === 'reply' && e.reply_text && e.reply_text.trim().length > 0).length },
    { name: 'Automation', icon: Mail, key: 'automation', badge: null },
    { name: 'Signals', icon: Radio, key: 'signals', badge: null },
    { name: 'Web Tracking', icon: Globe, key: 'web-tracking', badge: null },
    { name: 'Settings', icon: Settings, key: 'settings', badge: null },
    // Admin Panel - only visible to admin/developer
    { name: 'Admin Panel', icon: Shield, key: 'admin', badge: null, adminOnly: true },
  ];
  
  // Filter navigation based on RBAC - only show pages user can access
  const navigation = allNavigation.filter(item => {
    // Admin Panel only visible to admins/developers
    if (item.adminOnly) {
      return isAdminOrHigher();
    }
    // Other pages - check if user can access
    return canAccessPage(item.key);
  });

  // Show logout confirmation
  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
    setUserMenuOpen(false);
  };

  // Confirm and execute logout
  const confirmLogout = async () => {
    try {
      // Track logout event
      if (trackLogout) {
        trackLogout();
      }
      await logout();
    } catch (error) {
      logger.error('Logout failed:', error);
    } finally {
      setShowLogoutConfirm(false);
      // Force redirect to landing page to guarantee clean state
      window.location.href = '/';
    }
  };

  // Cancel logout
  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  // Get recent notifications
  const getRecentNotifications = () => {
    const notifications = [];
    
    // High-priority leads (score >= 20)
    const highPriorityLeads = leads.filter(lead => lead.score >= 20).slice(0, 3);
    highPriorityLeads.forEach(lead => {
      notifications.push({
        id: `lead-${lead.id}`,
        type: 'high-priority',
        title: 'High Priority Lead',
        message: `${lead.email} has reached ${lead.score} points`,
        time: lead.last_event_date || lead.created_at,
        icon: Star,
        color: 'text-red-600 bg-red-100'
      });
    });
    
    // Recent replies - only show VALID replies (AI-validated genuine replies)
    // Do not notify for auto-replies or out-of-office messages
    const recentReplies = leadEvents
      .filter(event => 
        event.event_type === 'reply' && 
        event.reply_text && 
        event.reply_text.trim().length > 0 &&
        (event.validation_status === 'valid' || event.validation_status === undefined || event.validation_status === null)
      )
      .slice(0, 3);
    recentReplies.forEach(event => {
      const isValidated = event.validation_status === 'valid';
      notifications.push({
        id: `reply-${event.id}`,
        type: 'reply',
        title: isValidated ? 'Valid Reply' : 'New Reply',
        message: `${event.lead_email} replied to your campaign`,
        time: event.created_at,
        icon: MessageSquare,
        color: isValidated ? 'text-green-600 bg-green-100' : 'text-blue-600 bg-blue-100',
        validationStatus: event.validation_status
      });
    });
    
    // Sort by time and return latest 5
    return notifications
      .sort((a, b) => new Date(b.time) - new Date(a.time))
      .slice(0, 5);
  };

  const allNotifications = getRecentNotifications();
  const unreadCount = getUnreadNotificationCount(allNotifications);

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationOpen && !event.target.closest('.notification-dropdown')) {
        setNotificationOpen(false);
      }
      // Close user menu when clicking outside
      if (userMenuOpen && userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notificationOpen, userMenuOpen]);

  // Check if user has chatbot access
  const hasChatbotAccess = hasPermission(PERMISSIONS.ACCESS_CHATBOT);

  // Get first letter for profile placeholder
  const getProfileInitial = () => {
    if (currentUser?.displayName) {
      return currentUser.displayName.charAt(0).toUpperCase();
    }
    if (currentUser?.email) {
      return currentUser.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Handle save display name
  const handleSaveDisplayName = async () => {
    if (!editNameValue.trim()) return;
    
    setIsSavingName(true);
    try {
      await updateDisplayName(editNameValue.trim());
      setIsEditingName(false);
      setEditNameValue('');
    } catch (error) {
      logger.error('Failed to update display name:', error);
      alert('Failed to update display name. Please try again.');
    } finally {
      setIsSavingName(false);
    }
  };

  // Start editing name
  const startEditingName = () => {
    setEditNameValue(currentUser?.displayName || '');
    setIsEditingName(true);
  };

  // Cancel editing name
  const cancelEditingName = () => {
    setIsEditingName(false);
    setEditNameValue('');
  };

  const sidebarVariants = {
    open: { x: 0 },
    closed: { x: '-100%' }
  };

  const desktopSidebarVariants = {
    open: { x: 0 },
    closed: { x: 0 }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.div
        variants={isDesktop ? desktopSidebarVariants : sidebarVariants}
        animate={isDesktop || sidebarOpen ? 'open' : 'closed'}
        transition={{ type: 'tween', duration: 0.3 }}
        className={`fixed inset-y-0 left-0 z-50 bg-white shadow-lg transform lg:transform-none lg:relative lg:z-auto transition-all duration-300 ${
          sidebarExpanded ? 'w-64' : 'w-16'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          <div className="flex items-center space-x-2 overflow-hidden">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0">
              <img 
                src="/Creative Gorilla logo.jpeg" 
                alt="CREATILLA Logo" 
                className="w-8 h-8 rounded-lg object-cover"
              />
            </div>
            <motion.span 
              initial={false}
              animate={{ opacity: sidebarExpanded ? 1 : 0, width: sidebarExpanded ? 'auto' : 0 }}
              transition={{ duration: 0.3 }}
              className="text-xl font-bold text-gray-900 whitespace-nowrap"
              style={{ fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
            >
              CREATILLA
            </motion.span>
          </div>
          <div className="flex items-center space-x-2">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setSidebarExpanded(!sidebarExpanded)}
              className="hidden lg:flex p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label={sidebarExpanded ? "Collapse sidebar" : "Expand sidebar"}
              title={sidebarExpanded ? "Collapse sidebar" : "Expand sidebar"}
            >
              {sidebarExpanded ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </motion.button>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
              aria-label="Close sidebar"
              title="Close sidebar"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <nav className="mt-8 px-2">
          <div className="space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = currentPage === item.key;
              
              return (
                <div key={item.name} className="relative group">
                  <motion.button
                    whileHover={{ scale: sidebarExpanded ? 1.02 : 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      onPageChange(item.key);
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center ${
                      sidebarExpanded ? 'justify-between px-4 py-3' : 'justify-center px-3 py-3'
                    } text-sm font-medium rounded-xl transition-all duration-200 group ${
                      isActive
                        ? 'bg-primary-600 text-white shadow-lg shadow-primary-600/25'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
                    }`}
                  >
                    <div className={`flex items-center ${
                      sidebarExpanded ? 'space-x-3' : 'justify-center'
                    }`}>
                      <Icon className={`w-5 h-5 transition-colors flex-shrink-0 ${
                        isActive 
                          ? 'text-white' 
                          : 'text-gray-500 group-hover:text-primary-600'
                      }`} />
                      <motion.span 
                        initial={false}
                        animate={{ 
                          opacity: sidebarExpanded ? 1 : 0,
                          width: sidebarExpanded ? 'auto' : 0
                        }}
                        transition={{ duration: 0.3 }}
                        className="font-medium whitespace-nowrap overflow-hidden"
                      >
                        {item.name}
                      </motion.span>
                    </div>
                    {sidebarExpanded && item.badge !== null && item.badge > 0 && (
                      <motion.span 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className={`text-xs px-2.5 py-1 rounded-full font-semibold ${
                          isActive
                            ? 'bg-white/20 text-white'
                            : 'bg-primary-100 text-primary-700'
                        }`}
                      >
                        {item.badge}
                      </motion.span>
                    )}
                  </motion.button>
                  
                  {/* Tooltip for collapsed state */}
                  {!sidebarExpanded && (
                    <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 flex items-center space-x-2">
                      <span>{item.name}</span>
                      {item.badge !== null && item.badge > 0 && (
                        <span className="bg-primary-600 text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                          {item.badge}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-2 border-t border-gray-200 bg-white">
          {sidebarExpanded ? (
            <>
              <div className="bg-gray-50 rounded-xl p-4 mb-3">
                <div className="flex items-center space-x-3">
                  {/* Profile Image with Fallback */}
                  {currentUser?.photoURL && !profileImageError ? (
                    <img 
                      src={currentUser.photoURL} 
                      alt="Profile" 
                      className="w-10 h-10 rounded-full object-cover shadow-lg ring-2 ring-white"
                      onError={() => setProfileImageError(true)}
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white">
                      <span className="text-white text-sm font-bold">
                        {getProfileInitial()}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">
                      {currentUser?.displayName || currentUser?.email?.split('@')[0]}
                    </p>
                    {/* Real role from RBAC */}
                    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${getRoleBadgeColor(userRole)}`}>
                      {getRoleDisplayName(userRole)}
                    </span>
                  </div>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.02, x: 4 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleLogoutClick}
                className="w-full flex items-center space-x-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all duration-200 group"
              >
                <LogOut className="w-4 h-4 group-hover:text-red-600 transition-colors" />
                <span>Sign out</span>
              </motion.button>
            </>
          ) : (
            <div className="flex flex-col items-center space-y-2">
              <div className="relative group">
                {/* Profile Image with Fallback - Collapsed */}
                {currentUser?.photoURL && !profileImageError ? (
                  <img 
                    src={currentUser.photoURL} 
                    alt="Profile" 
                    className="w-10 h-10 rounded-full object-cover shadow-lg ring-2 ring-white"
                    onError={() => setProfileImageError(true)}
                  />
                ) : (
                  <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white">
                    <span className="text-white text-sm font-bold">
                      {getProfileInitial()}
                    </span>
                  </div>
                )}
                <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
                  <p className="font-medium">{currentUser?.displayName || currentUser?.email}</p>
                  <p className="text-xs text-gray-400">{getRoleDisplayName(userRole)}</p>
                </div>
              </div>
              <div className="relative group">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleLogoutClick}
                  className="p-2 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg transition-all duration-200"
                >
                  <LogOut className="w-4 h-4" />
                </motion.button>
                <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
                  Sign out
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden transition-all duration-300">
        {/* Top bar */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center space-x-4">
              {/* Mobile menu button - only visible on small screens */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
                aria-label="Open navigation menu"
              >
                <Menu className="w-6 h-6" />
              </motion.button>
            </div>

            <div className="flex items-center space-x-3">
              {/* Search bar - hidden on mobile */}
              <div className="hidden md:block relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-64 text-sm bg-gray-50 focus:bg-white transition-colors"
                />
              </div>

              {/* Notifications */}
              <div className="relative notification-dropdown">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setNotificationOpen(!notificationOpen)}
                  className="relative p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                  aria-label="View notifications"
                  aria-expanded={notificationOpen}
                  aria-haspopup="true"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <motion.span 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-semibold shadow-lg"
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </motion.span>
                  )}
                </motion.button>

                {/* Notification Dropdown */}
                <AnimatePresence>
                  {notificationOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-50 max-h-96 overflow-hidden"
                    >
                      <div className="p-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                          {unreadCount > 0 && (
                            <button
                              onClick={() => {
                                markAllNotificationsAsRead(allNotifications.map(n => n.id));
                                setNotificationOpen(false);
                              }}
                              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                              aria-label="Mark all notifications as read"
                            >
                              Mark all read
                            </button>
                          )}
                        </div>
                      </div>
                      
                      <div className="max-h-80 overflow-y-auto">
                        {allNotifications.length > 0 ? (
                          <div className="divide-y divide-gray-100">
                            {allNotifications.map((notification) => {
                              const IconComponent = notification.icon;
                              const isRead = isNotificationRead(notification.id);
                              return (
                                <motion.div
                                  key={notification.id}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer relative ${
                                    isRead ? 'opacity-60' : ''
                                  }`}
                                  onClick={() => {
                                    // Mark notification as read
                                    markNotificationAsRead(notification.id);
                                    
                                    // Handle notification click and redirect to specific lead
                                    if (notification.type === 'high-priority') {
                                      // Extract lead ID from notification ID (format: "lead-{leadId}")
                                      const leadId = notification.id.replace('lead-', '');
                                      const lead = leads.find(l => l.id === leadId);
                                      
                                      if (lead) {
                                        // Dispatch viewLead event to open specific lead details
                                        window.dispatchEvent(new CustomEvent('viewLead', {
                                          detail: { lead }
                                        }));
                                      } else {
                                        // Fallback to calllist page if lead not found
                                        onPageChange('calllist');
                                      }
                                    } else if (notification.type === 'reply') {
                                      // Extract lead email from notification message or find by reply event
                                      const replyId = notification.id.replace('reply-', '');
                                      const replyEvent = leadEvents.find(event => event.id === replyId);
                                      
                                      if (replyEvent) {
                                        const lead = leads.find(l => l.email === replyEvent.lead_email);
                                        if (lead) {
                                          // Dispatch viewLead event to open specific lead details
                                          window.dispatchEvent(new CustomEvent('viewLead', {
                                            detail: { lead }
                                          }));
                                        } else {
                                          // Fallback to leads page if lead not found
                                          onPageChange('leads');
                                        }
                                      } else {
                                        // Fallback to leads page if event not found
                                        onPageChange('leads');
                                      }
                                    }
                                    setNotificationOpen(false);
                                  }}
                                >
                                  {!isRead && (
                                    <div className="absolute left-2 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-blue-500 rounded-full"></div>
                                  )}
                                  <div className={`flex items-start space-x-3 ${!isRead ? 'ml-4' : ''}`}>
                                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${notification.color}`}>
                                      <IconComponent className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900">
                                        {notification.title}
                                      </p>
                                      <p className="text-sm text-gray-600 truncate">
                                        {notification.message}
                                      </p>
                                      <div className="flex items-center mt-1 text-xs text-gray-500">
                                        <Calendar className="w-3 h-3 mr-1" />
                                        <span>{new Date(notification.time).toLocaleString()}</span>
                                      </div>
                                    </div>
                                  </div>
                                </motion.div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="p-8 text-center">
                            <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-sm font-medium text-gray-900 mb-1">No notifications</h3>
                            <p className="text-sm text-gray-500">You're all caught up!</p>
                          </div>
                        )}
                      </div>
                      
                      {allNotifications.length > 0 && (
                        <div className="p-3 border-t border-gray-200 bg-gray-50">
                          <button
                            onClick={() => {
                              onPageChange('dashboard');
                              setNotificationOpen(false);
                            }}
                            className="w-full text-center text-sm text-primary-600 hover:text-primary-700 font-medium py-1"
                            aria-label="View all notifications on dashboard"
                          >
                            View all on Dashboard
                          </button>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* User Menu Dropdown */}
              <div className="relative" ref={userMenuRef}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 p-1 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  {/* Profile Image with Fallback */}
                  {currentUser?.photoURL && !profileImageError ? (
                    <img 
                      src={currentUser.photoURL} 
                      alt="Profile" 
                      className="w-9 h-9 rounded-full object-cover shadow-lg ring-2 ring-white"
                      onError={() => setProfileImageError(true)}
                    />
                  ) : (
                    <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white">
                      <span className="text-white text-sm font-bold">
                        {getProfileInitial()}
                      </span>
                    </div>
                  )}
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </motion.button>

                {/* User Menu Dropdown */}
                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden"
                    >
                      {/* User Info Header */}
                      <div className="p-4 bg-gradient-to-r from-primary-50 to-blue-50 border-b border-gray-100">
                        <div className="flex items-center space-x-3">
                          {/* Profile Image with Fallback */}
                          {currentUser?.photoURL && !profileImageError ? (
                            <img 
                              src={currentUser.photoURL} 
                              alt="Profile" 
                              className="w-14 h-14 rounded-full object-cover shadow-md ring-2 ring-white"
                              onError={() => setProfileImageError(true)}
                            />
                          ) : (
                            <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center shadow-md ring-2 ring-white">
                              <span className="text-white text-xl font-bold">
                                {getProfileInitial()}
                              </span>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            {/* Display Name with Inline Edit */}
                            {isEditingName ? (
                              <div className="flex items-center space-x-2">
                                <input
                                  type="text"
                                  value={editNameValue}
                                  onChange={(e) => setEditNameValue(e.target.value)}
                                  placeholder="Enter display name"
                                  className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveDisplayName();
                                    if (e.key === 'Escape') cancelEditingName();
                                  }}
                                  disabled={isSavingName}
                                />
                                <button
                                  onClick={handleSaveDisplayName}
                                  disabled={isSavingName || !editNameValue.trim()}
                                  className="p-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {isSavingName ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Check className="w-4 h-4" />
                                  )}
                                </button>
                                <button
                                  onClick={cancelEditingName}
                                  disabled={isSavingName}
                                  className="p-1.5 bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-2">
                                <p className="text-sm font-semibold text-gray-900 truncate">
                                  {currentUser?.displayName || 'Click to set name'}
                                </p>
                                <button
                                  onClick={startEditingName}
                                  className="p-1 text-gray-400 hover:text-primary-600 hover:bg-white rounded transition-colors"
                                  title="Edit display name"
                                >
                                  <Edit3 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                            <p className="text-xs text-gray-500 truncate mt-0.5">{currentUser?.email}</p>
                            <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full mt-1 ${getRoleBadgeColor(userRole)}`}>
                              {getRoleDisplayName(userRole)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Menu Items */}
                      <div className="p-2">
                        {/* Profile/Settings Link */}
                        <button
                          onClick={() => {
                            onPageChange('settings');
                            setUserMenuOpen(false);
                            setIsEditingName(false);
                          }}
                          className="w-full flex items-center space-x-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          <User className="w-4 h-4 text-gray-400" />
                          <span>Profile Settings</span>
                        </button>

                        {/* Change Password - navigates to Settings > Security */}
                        <button
                          onClick={() => {
                            onPageChange('settings');
                            setUserMenuOpen(false);
                            setIsEditingName(false);
                            // Small delay to ensure page renders before switching tab
                            setTimeout(() => {
                              const securityTab = document.querySelector('[data-tab="security"]');
                              if (securityTab) securityTab.click();
                            }, 100);
                          }}
                          className="w-full flex items-center space-x-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          <Lock className="w-4 h-4 text-gray-400" />
                          <span>Change Password</span>
                        </button>

                        {/* Divider */}
                        <div className="my-2 border-t border-gray-100"></div>

                        {/* Sign Out */}
                        <button
                          onClick={handleLogoutClick}
                          className="w-full flex items-center space-x-3 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </header>

        {/* Impersonation Banner - shown when viewing as another user */}
        {isImpersonating && impersonatedUser && (
          <div className="bg-blue-600 text-white px-4 py-3 shadow-lg">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Eye className="w-5 h-5" />
                <span className="font-medium">
                  Viewing as: <span className="font-bold">{impersonatedUser.email}</span>
                </span>
                <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-medium">
                  {getRoleDisplayName(impersonatedUser.role)}
                </span>
              </div>
              <button
                onClick={stopImpersonation}
                className="flex items-center space-x-2 px-4 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors font-medium text-sm"
              >
                <XCircle className="w-4 h-4" />
                <span>Exit User View</span>
              </button>
            </div>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 overflow-auto bg-gray-50">
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="h-full"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
              {children}
            </div>
          </motion.div>
        </main>
      </div>
      
      {/* Floating Chat Agent - Only visible when user has chatbot access */}
      {hasChatbotAccess && (
        <div className="fixed bottom-4 right-4 z-50">
          {/* Chat Interface */}
          <AnimatePresence>
            {chatOpen && (
              <motion.div
                initial={{ 
                  opacity: 0, 
                  scale: 0.3, 
                  height: 0,
                  y: 50,
                  transformOrigin: "bottom right"
                }}
                animate={{ 
                  opacity: 1, 
                  scale: 1, 
                  height: 600,
                  y: 0,
                  transformOrigin: "bottom right"
                }}
                exit={{ 
                  opacity: 0, 
                  scale: 0.3, 
                  height: 0,
                  y: 50,
                  transformOrigin: "bottom right"
                }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                  mass: 0.8
                }}
                className="absolute bottom-16 right-0 w-96 bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden"
              >
                <ChatAgent 
                  isMinimized={false}
                  onToggleMinimize={() => setChatOpen(false)}
                  isFloating={true}
                />
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Chat Bubble Button */}
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1, rotate: chatOpen ? 180 : 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setChatOpen(!chatOpen)}
            className={`w-14 h-14 ${chatOpen ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 relative`}
          >
            <motion.div
              animate={{ rotate: chatOpen ? 0 : 0 }}
              transition={{ duration: 0.3 }}
            >
              {chatOpen ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
            </motion.div>
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
              AI
            </div>
          </motion.button>
        </div>
      )}
      
      {/* Logout Confirmation Modal */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={cancelLogout}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Animated Header */}
              <div className="relative bg-gradient-to-r from-red-500 to-red-600 p-6 text-center overflow-hidden">
                {/* Animated background circles */}
                <motion.div
                  animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute top-0 left-0 w-32 h-32 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2"
                />
                <motion.div
                  animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="absolute bottom-0 right-0 w-24 h-24 bg-white/10 rounded-full translate-x-1/2 translate-y-1/2"
                />
                
                {/* Logout Icon */}
                <motion.div
                  initial={{ rotate: -180, scale: 0 }}
                  animate={{ rotate: 0, scale: 1 }}
                  transition={{ type: 'spring', damping: 15, delay: 0.1 }}
                  className="relative inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full mb-4"
                >
                  <motion.div
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <LogOut className="w-10 h-10 text-white" />
                  </motion.div>
                </motion.div>
                
                <motion.h3
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-xl font-bold text-white"
                >
                  Sign Out?
                </motion.h3>
              </div>

              {/* Content */}
              <div className="p-6">
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-gray-600 text-center mb-6"
                >
                  Are you sure you want to sign out of your account?
                </motion.p>

                {/* User Info */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="flex items-center justify-center space-x-3 p-3 bg-gray-50 rounded-xl mb-6"
                >
                  {currentUser?.photoURL && !profileImageError ? (
                    <img
                      src={currentUser.photoURL}
                      alt="Profile"
                      className="w-10 h-10 rounded-full object-cover"
                      onError={() => setProfileImageError(true)}
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-bold">{getProfileInitial()}</span>
                    </div>
                  )}
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-900">
                      {currentUser?.displayName || currentUser?.email?.split('@')[0]}
                    </p>
                    <p className="text-xs text-gray-500">{currentUser?.email}</p>
                  </div>
                </motion.div>

                {/* Buttons */}
                <div className="flex space-x-3">
                  <motion.button
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={cancelLogout}
                    className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={confirmLogout}
                    className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors flex items-center justify-center space-x-2"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Layout;